import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { Form, Select } from 'formik-antd';
import { Col, Row, Button, Table, message } from 'antd';
import { CloudUploadOutlined, EyeOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';
import styled from 'styled-components';
import { FormInput, CustomInput, Flex } from 'atoms';
import { searchPincodeV2 } from 'store/clientSliceV2';
import { createNode, updateNode } from 'store/nodeSliceV2';
import AddressPickerV2 from 'components/common/AddressPickerV2/AddressPickerV2';
import loadGoogleMapsV2 from 'utils/loadGoogleMapsV2';
import { truncateAddressToCity } from 'utils/addressHelpers';
import {
	FormContainer,
	DisabledInput,
	SaveButton,
	NextButton,
	CancelButton,
} from '../client/clientFormStyles';
import { BulkUploadModal } from 'componentsv2/modals';
import NodeActionModal from './NodeActionModal';

// Node Type Options
const NODE_TYPE_OPTIONS = [
	{ value: 'Warehouse', label: 'Warehouse' },
	{ value: 'Store', label: 'Store' },
];

// Bulk Upload Button
const BulkUploadButton = styled(Button)`
	color: #18405d;
	border: 1px solid #18405d;
	height: 40px;
	padding: 0 24px;
	display: flex;
	align-items: center;
	gap: 8px;
	font-weight: 500;

	&:hover {
		background: #18405d;
		color: #fff;
	}
`;

// Add Node Button
const AddNodeButton = styled(Button)`
	background: #18405d;
	color: #fff;
	border: none;
	height: 40px;
	padding: 0 24px;
	font-weight: 500;
	margin-top: 24px;

	&:hover {
		background: #1a4a6e;
		color: #fff;
	}
`;

// Previous Button (dark blue #18405D)
const PreviousButton = styled(Button)`
	background: #18405d;
	color: #fff;
	border: none;
	min-width: 120px;
	height: 40px;
	font-weight: 500;

	&:hover {
		background: #1a4a6e;
		color: #fff;
	}
`;

// Table Title
const TableTitle = styled.h3`
	font-size: 18px;
	font-weight: 600;
	color: #18405d;
	margin: 32px 0 16px 0;
`;

// Styled Table
const StyledTable = styled(Table)`
	.ant-table-thead > tr > th {
		background: #18405d;
		color: #fff;
		font-weight: 600;
		padding: 12px 16px;
	}
	.ant-table-tbody > tr > td {
		border-bottom: 1px solid #f0f0f0;
		padding: 12px 16px;
	}
	.ant-table-tbody > tr:hover > td {
		background: #f5f5f5;
	}
`;

// Validation Schema
const NodeInfoSchema = Yup.object().shape({
	project_name: Yup.string().required('Project name is required'),
	node_type: Yup.string().required('Node type is required'),
	node_name: Yup.string()
		.required('Node name is required')
		.min(2, 'Node name must be at least 2 characters')
		.max(100, 'Node name must not exceed 100 characters'),
	node_address: Yup.string()
		.required('Node address is required')
		.min(5, 'Node address must be at least 5 characters'),
	latitude: Yup.string()
		.required('Latitude is required')
		.matches(/^[\d°.-\s]+$/, 'Invalid latitude format'),
	longitude: Yup.string()
		.required('Longitude is required')
		.matches(/^[\d°.-\s]+$/, 'Invalid longitude format'),
	pincode: Yup.string()
		.required('Pincode is required')
		.matches(/^\d{6}$/, 'Pincode must be exactly 6 digits'),
	poc_name: Yup.string().max(100, 'PoC name must not exceed 100 characters'),
	poc_number: Yup.string().test(
		'valid-mobile',
		'Mobile number is invalid. Please enter a 10-digit mobile number starting with 6-9',
		(value) => !value || /^[6-9][0-9]{9}$/.test(value)
	),
});

export default function NodeInformationFormV2({
	projectInfoData,
	nodes,
	setNodes,
	setCurrentStep,
	isEditMode,
	isViewMode,
	onSubmit,
	onCancel,
	loading,
	savedProjectId,
	nodesSaved,
	setNodesSaved,
}) {
	const dispatch = useDispatch();
	const [formKey, setFormKey] = useState(0);
	const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState(false);
	const [uploadLoading, setUploadLoading] = useState(false);
	const [nodeActionModalVisible, setNodeActionModalVisible] = useState(false);
	const [selectedNode, setSelectedNode] = useState(null);

	// Load Google Maps on component mount
	useEffect(() => {
		loadGoogleMapsV2().catch((err) => {
			console.error('Failed to load Google Maps:', err);
			message.error('Failed to load Google Maps. Please refresh the page.');
		});
	}, []);

	// Initial Values
	const initialValues = useMemo(
		() => ({
			project_name: projectInfoData?.project_name || '',
			node_type: undefined,
			node_name: '',
			node_address: '',
			latitude: '',
			longitude: '',
			poc_name: '',
			poc_number: '',
			pincode: '',
			zone: '',
			city: '',
			state: '',
		}),
		[projectInfoData]
	);

	// Handle pincode lookup - Auto-fetch zone, city, state when user types 6-digit pincode
	const handlePincodeLookup = async (pincode, setFieldValue) => {
		if (pincode && pincode.length === 6 && /^\d{6}$/.test(pincode)) {
			try {
				const result = await dispatch(searchPincodeV2({ pincode }));
				if (result?.zone) {
					setFieldValue('zone', result.zone);
				}
				if (result?.city) {
					setFieldValue('city', result.city);
				}
				if (result?.state) {
					setFieldValue('state', result.state);
				}
			} catch (error) {
				// Error already handled by the searchPincodeV2 action
			}
		}
	};

	// Handle Add Node
	const handleAddNode = (values, { resetForm, setSubmitting }) => {
		const newNode = {
			id: Date.now(), // Temporary ID
			project_name: projectInfoData?.project_name || values.project_name,
			node_type: values.node_type,
			node_name: values.node_name,
			node_address: values.node_address,
			latitude: values.latitude,
			longitude: values.longitude,
			poc_name: values.poc_name || '',
			poc_number: values.poc_number || '',
			pincode: values.pincode,
			zone: values.zone || '',
			city: values.city || '',
			state: values.state || '',
		};

		setNodes([...nodes, newNode]);
		// Reset nodesSaved when node is added
		if (setNodesSaved) {
			setNodesSaved(false);
		}
		message.success('Node added successfully');
		resetForm();
		setFormKey((prev) => prev + 1);
		setSubmitting(false);
	};

	// Handle View Node - Open Modal
	const handleViewNode = (node) => {
		setSelectedNode(node);
		setNodeActionModalVisible(true);
	};

	// Handle Edit Node
	const handleEditNode = (nodeData) => {
		const updatedNodes = nodes.map((node) => {
			if (node.id === nodeData.id) {
				return { ...node, ...nodeData };
			}
			return node;
		});
		setNodes(updatedNodes);
		// Reset nodesSaved when node is edited
		if (setNodesSaved) {
			setNodesSaved(false);
		}
		message.success('Node updated successfully');
	};

	// Handle Delete Node
	const handleDeleteNode = (nodeId) => {
		const updatedNodes = nodes.filter((node) => node.id !== nodeId);
		setNodes(updatedNodes);
		// Reset nodesSaved when node is deleted
		if (setNodesSaved) {
			setNodesSaved(false);
		}
		message.success('Node deleted successfully');
	};

	// Handle Bulk Upload - Open Modal
	const handleBulkUpload = () => {
		setBulkUploadModalVisible(true);
	};

	// Handle Bulk Upload File Submit
	const handleBulkUploadSubmit = async (file) => {
		setUploadLoading(true);
		try {
			// TODO: Implement API call to process bulk upload

			// Simulate API call (will be replaced with actual API call)
			const simulateUpload = () =>
				new Promise((resolve) => {
					setTimeout(() => resolve(), 2000);
				});
			await simulateUpload();

			message.success(`File "${file.name}" uploaded successfully!`);
			setBulkUploadModalVisible(false);

			// TODO: After successful upload, fetch the nodes from API response
			// and add them to the nodes array
			// For now, just showing success message
		} catch (error) {
			message.error('Failed to upload file. Please try again.');
		} finally {
			setUploadLoading(false);
		}
	};

	// Handle Close Bulk Upload Modal
	const handleCloseBulkUploadModal = () => {
		setBulkUploadModalVisible(false);
	};

	// Handle Previous
	const handlePrevious = () => {
		setCurrentStep(0);
	};

	// Helper function to prepare node data for API
	const prepareNodeDataForApi = (node) => {
		// Extract line1 from node_address - handle both string and object formats
		let line1 = '';
		if (typeof node.node_address === 'string') {
			line1 = node.node_address;
		} else if (node.node_address && typeof node.node_address === 'object') {
			line1 = node.node_address.line1 || '';
		}

		// Extract city, state, pincode from node_address object if available, otherwise use node properties
		const city = node.node_address?.city || node.city || '';
		const state = node.node_address?.state || node.state || '';
		const pincode = node.node_address?.pincode || node.pincode || '';

		return {
			project_id: savedProjectId,
			node_type: node.node_type,
			node_name: node.node_name,
			node_address: {
				line1,
				line2: node.node_address?.line2 || '',
				city,
				state,
				pincode,
				country: node.node_address?.country || 'India',
			},
			latitude: parseFloat(node.latitude),
			longitude: parseFloat(node.longitude),
			pincode,
			poc_name: node.poc_name || '',
			poc_mobile_number: node.poc_number || '',
			poc_email: '',
		};
	};

	// Helper: Submit a single node (create or update)
	const submitSingleNode = async (node) => {
		try {
			const nodeData = prepareNodeDataForApi(node);

			// Check if node has a backend ID (exists in database)
			// Backend IDs are typically less than 1000000000000 (temporary IDs are larger)
			const hasBackendId = node.id && node.id < 1000000000000;

			let result;
			if (hasBackendId) {
				// Update existing node
				result = await dispatch(updateNode({ req_data: { ...nodeData, id: node.id } }));
			} else {
				// Create new node
				result = await dispatch(createNode({ req_data: nodeData }));
			}

			// Update node with backend ID if returned
			if (result?.data?.id) {
				return { success: true, nodeName: node.node_name, nodeId: result.data.id };
			}
			return { success: true, nodeName: node.node_name };
		} catch (error) {
			const errorMsg = error?.response?.data?.error?.message || error.message;
			const errorCode = error?.response?.data?.error?.code;

			if (errorCode === 'DUPLICATE_ADDRESS') {
				message.error(`Node "${node.node_name}": ${errorMsg}`);
			}

			return { success: false, nodeName: node.node_name, error: errorMsg };
		}
	};

	// Helper: Update nodes with backend IDs
	const updateNodesWithBackendIds = (processedResults) => {
		const updatedNodes = nodes.map((node, index) => {
			const result = processedResults[index];
			if (result?.success && result?.nodeId) {
				return { ...node, id: result.nodeId };
			}
			return node;
		});
		setNodes(updatedNodes);
	};

	// Helper: Show save results
	const showSaveResults = (processedResults) => {
		const successCount = processedResults.filter((r) => r.success).length;
		const failedCount = nodes.length - successCount;

		if (successCount === nodes.length) {
			message.success(`All ${successCount} node(s) saved successfully`);
		} else if (successCount > 0) {
			message.warning(`${successCount} node(s) saved, ${failedCount} failed`);
		} else {
			message.error('Failed to save any nodes. Please check the errors and try again.');
		}
	};

	// Render navigation buttons
	const renderNavigationButtons = () => {
		if (isViewMode) {
			return null;
		}

		if (isEditMode) {
			// Edit mode: Cancel, Save, and Save Changes buttons
			return (
				<>
					<CancelButton onClick={onCancel}>Cancel</CancelButton>
					{savedProjectId && (
						<SaveButton
							type="button"
							onClick={handleSaveNodes}
							style={{ marginRight: 12, marginLeft: 12 }}
							loading={uploadLoading}
							disabled={uploadLoading}
						>
							Save
						</SaveButton>
					)}
					<SaveButton
						type="button"
						onClick={() => handleFinalSubmit(() => {})}
						loading={loading}
						disabled={loading}
					>
						Save Changes
					</SaveButton>
				</>
			);
		}

		// Create mode: Save and Submit buttons
		return (
			<>
				{savedProjectId && (
					<SaveButton
						type="button"
						onClick={handleSaveNodes}
						loading={uploadLoading}
						disabled={uploadLoading}
						style={{ marginRight: 12 }}
					>
						Save
					</SaveButton>
				)}
				<NextButton
					type="button"
					onClick={() => handleFinalSubmit(() => {})}
					loading={loading}
					disabled={loading}
				>
					Submit
				</NextButton>
			</>
		);
	};

	// Handle Save Nodes (creates nodes individually but stays on page)
	const handleSaveNodes = async () => {
		if (!savedProjectId) {
			message.warning('Please save the project first before saving nodes');
			return;
		}

		if (nodes.length === 0) {
			message.warning('Please add at least one node before saving');
			return;
		}

		setUploadLoading(true);
		try {
			// Submit all nodes in parallel using Promise.allSettled
			const nodePromises = nodes.map((node) => submitSingleNode(node));
			const results = await Promise.allSettled(nodePromises);
			const processedResults = results.map((result) =>
				result.status === 'fulfilled' ? result.value : { success: false }
			);

			updateNodesWithBackendIds(processedResults);
			showSaveResults(processedResults);

			// Set nodesSaved to true if all nodes were successfully saved
			const successCount = processedResults.filter((r) => r.success).length;
			if (successCount === nodes.length && setNodesSaved) {
				setNodesSaved(true);
			}
		} catch (error) {
			message.error('An unexpected error occurred during node save');
			console.error('Node save error:', error);
		} finally {
			setUploadLoading(false);
		}
	};

	// Handle Final Submit
	const handleFinalSubmit = async (setErrors) => {
		if (nodes.length === 0) {
			message.warning('Please add at least one node');
			return;
		}

		// Validation: In create mode, both project and nodes must be saved before submission
		if (!isEditMode && !isViewMode) {
			if (!savedProjectId) {
				message.error('You need to click the save button');
				return;
			}
			if (!nodesSaved) {
				message.error('You need to click the save button');
				return;
			}
		}

		// If project was already saved, skip project create and just navigate
		// Pass projectInfoData so parent can handle navigation
		if (!isEditMode && savedProjectId) {
			// Just navigate to project list (project already created)
			// Pass projectInfoData for consistency, but parent will skip API call
			await onSubmit(projectInfoData, setErrors);
			return;
		}

		const nodeData = {
			nodes: nodes.map((node) => ({
				node_type: node.node_type,
				node_name: node.node_name,
				node_address: node.node_address,
				latitude: node.latitude,
				longitude: node.longitude,
				poc_name: node.poc_name || undefined,
				poc_number: node.poc_number || undefined,
				pin_code: node.pincode,
				zone: node.zone || undefined,
				city: node.city || undefined,
				state: node.state || undefined,
			})),
		};

		// Combine project info and node data
		const combinedData = { ...projectInfoData, ...nodeData };
		await onSubmit(combinedData, setErrors);
	};

	// Table Columns
	const columns = [
		{
			title: 'Project Name',
			dataIndex: 'project_name',
			key: 'project_name',
		},
		{
			title: 'Node Type',
			dataIndex: 'node_type',
			key: 'node_type',
		},
		{
			title: 'Node Name',
			dataIndex: 'node_name',
			key: 'node_name',
		},
		{
			title: 'Node Address',
			dataIndex: 'node_address',
			key: 'node_address',
			render: (nodeAddress) => {
				if (!nodeAddress) return '-';
				if (typeof nodeAddress === 'string') return nodeAddress;
				if (typeof nodeAddress === 'object' && nodeAddress.line1) {
					return nodeAddress.line1;
				}
				return '-';
			},
		},
		{
			title: 'Coordinates',
			key: 'coordinates',
			render: (_, record) => `${record.latitude}, ${record.longitude}`,
		},
		{
			title: 'PoC Name',
			dataIndex: 'poc_name',
			key: 'poc_name',
		},
		{
			title: 'PoC Number',
			dataIndex: 'poc_number',
			key: 'poc_number',
		},
		{
			title: 'Pincode',
			dataIndex: 'pincode',
			key: 'pincode',
		},
		...(!isViewMode
			? [
					{
						title: 'Actions',
						key: 'actions',
						render: (_, record) => (
							<Button
								type="link"
								icon={<EyeOutlined />}
								onClick={() => handleViewNode(record)}
								style={{ color: '#18405d' }}
							>
								View
							</Button>
						),
					},
			  ]
			: []),
	];

	return (
		<>
			{/* Bulk Upload Button - Outside the form box, between stepper and form */}
			{!isViewMode && (
				<Flex justifyContent="flex-end" mb={3}>
					<BulkUploadButton icon={<CloudUploadOutlined />} onClick={handleBulkUpload}>
						Bulk Upload
					</BulkUploadButton>
				</Flex>
			)}

			{/* Node Information Form - In white box */}
			<FormContainer>
				<Formik
					key={formKey}
					initialValues={initialValues}
					validationSchema={NodeInfoSchema}
					onSubmit={handleAddNode}
					enableReinitialize
				>
					{({ setFieldValue, values, isSubmitting, submitForm }) => (
						<Form>
							{/* Row 1: Project Name | Node Type */}
							<Row gutter={24}>
								<Col span={12}>
									<FormInput label="Project Name" name="project_name" required>
										<DisabledInput
											name="project_name"
											value={projectInfoData?.project_name || ''}
											placeholder={
												projectInfoData?.project_name || 'Project Name'
											}
											disabled
										/>
									</FormInput>
								</Col>
								<Col span={12}>
									<FormInput label="Node Type" name="node_type" required>
										<Select
											name="node_type"
											placeholder="Select"
											options={NODE_TYPE_OPTIONS}
											style={{ width: '100%' }}
											disabled={isViewMode}
										/>
									</FormInput>
								</Col>
							</Row>

							{/* Row 2: Node Name | Node Address */}
							<Row gutter={24}>
								<Col span={12}>
									<FormInput label="Node Name" name="node_name" required>
										<CustomInput
											name="node_name"
											placeholder="Eg : Li"
											disabled={isViewMode}
										/>
									</FormInput>
								</Col>
								<Col span={12}>
									<FormInput label="Node Address" name="node_address" required>
										<AddressPickerV2
											value={values.node_address || ''}
											latitude={values.latitude || ''}
											longitude={values.longitude || ''}
											onChange={(data) => {
												// Truncate address to city (safeguard - AddressPickerV2 already truncates, but ensure consistency)
												const truncatedAddress = truncateAddressToCity(
													data.address,
													data.city || ''
												);
												setFieldValue('node_address', truncatedAddress);
												setFieldValue('latitude', data.latitude);
												setFieldValue('longitude', data.longitude);
												// Optionally update state and pincode if they exist in form
												if (data.state) {
													setFieldValue('state', data.state);
												}
												if (data.pincode) {
													setFieldValue('pincode', data.pincode);
													// Auto-fetch zone and city if pincode is valid
													if (
														data.pincode.length === 6 &&
														/^\d{6}$/.test(data.pincode)
													) {
														handlePincodeLookup(
															data.pincode,
															setFieldValue
														);
													}
												}
											}}
											disabled={isViewMode}
											placeholder="Eg : Intelligent Retail...."
										/>
									</FormInput>
								</Col>
							</Row>

							{/* Row 3: Latitude and Longitude - auto-populated by AddressPicker */}
							<Row gutter={24}>
								<Col span={12}>
									<FormInput label="Latitude" name="latitude" required>
										<CustomInput
											name="latitude"
											disabled={isViewMode}
											placeholder="Auto-filled from address"
											readOnly
										/>
									</FormInput>
								</Col>
								<Col span={12}>
									<FormInput label="Longitude" name="longitude" required>
										<CustomInput
											name="longitude"
											disabled={isViewMode}
											placeholder="Auto-filled from address"
											readOnly
										/>
									</FormInput>
								</Col>
							</Row>

							{/* Row 4: PoC Name | PoC Number */}
							<Row gutter={24}>
								<Col span={12}>
									<FormInput label="PoC Name" name="poc_name">
										<CustomInput
											name="poc_name"
											placeholder="Eg : AB 01 AB 1234"
											disabled={isViewMode}
										/>
									</FormInput>
								</Col>
								<Col span={12}>
									<FormInput label="PoC Mobile Number" name="poc_number">
										<CustomInput
											name="poc_number"
											placeholder="Eg : 9876543210"
											disabled={isViewMode}
										/>
									</FormInput>
								</Col>
							</Row>

							{/* Row 5: Pincode | Zone | City | State (all 4 fields in single line) */}
							<Row gutter={24}>
								<Col span={6}>
									<FormInput label="Pincode" name="pincode" required>
										<CustomInput
											name="pincode"
											placeholder="Eg : 517408"
											disabled={isViewMode}
											onChange={(e) => {
												setFieldValue('pincode', e.target.value);
												handlePincodeLookup(e.target.value, setFieldValue);
											}}
										/>
									</FormInput>
								</Col>
								<Col span={6}>
									<FormInput label="Zone" name="zone">
										<DisabledInput
											name="zone"
											placeholder={values.zone || 'South'}
											disabled
										/>
									</FormInput>
								</Col>
								<Col span={6}>
									<FormInput label="City" name="city">
										<DisabledInput
											name="city"
											placeholder={values.city || 'South'}
											disabled
										/>
									</FormInput>
								</Col>
								<Col span={6}>
									<FormInput label="State" name="state">
										<DisabledInput
											name="state"
											placeholder={values.state || 'South'}
											disabled
										/>
									</FormInput>
								</Col>
							</Row>

							{/* Add Node Button - Hide in view mode */}
							{!isViewMode && (
								<Flex justifyContent="center">
									<AddNodeButton
										type="button"
										onClick={() => {
											submitForm();
										}}
										disabled={isSubmitting}
									>
										{isSubmitting ? 'Adding...' : 'Add Node'}
									</AddNodeButton>
								</Flex>
							)}
						</Form>
					)}
				</Formik>
			</FormContainer>

			{/* Node Details Table - In separate white box */}
			<FormContainer style={{ marginTop: 24 }}>
				<TableTitle>Node Details</TableTitle>
				<StyledTable
					columns={columns}
					dataSource={nodes}
					rowKey="id"
					pagination={false}
					bordered
					locale={{
						emptyText: 'No nodes added yet. Add nodes using the form above.',
					}}
				/>
			</FormContainer>

			{/* Navigation Buttons - Outside the white box */}
			<Flex justifyContent="space-between" gap={12} mt={4}>
				<PreviousButton onClick={handlePrevious}>Previous</PreviousButton>
				<Flex gap={12}>{renderNavigationButtons()}</Flex>
			</Flex>

			{/* Bulk Upload Modal */}
			<BulkUploadModal
				visible={bulkUploadModalVisible}
				onClose={handleCloseBulkUploadModal}
				onSubmit={handleBulkUploadSubmit}
				title="Bulk Upload Nodes"
				description="Upload a CSV or Excel file containing node information. The file should include columns for node type, name, address, coordinates, PoC details, and pincode."
				templateUrl="/node_bulk_upload_template_v2.csv"
				acceptedFormats=".csv,.xls,.xlsx"
				maxSizeMB={10}
				loading={uploadLoading}
			/>

			{/* Node Action Modal */}
			<NodeActionModal
				open={nodeActionModalVisible}
				onCancel={() => {
					setNodeActionModalVisible(false);
					setSelectedNode(null);
				}}
				nodeData={selectedNode}
				onEdit={handleEditNode}
				onDelete={handleDeleteNode}
			/>
		</>
	);
}

NodeInformationFormV2.propTypes = {
	projectInfoData: PropTypes.object.isRequired,
	nodes: PropTypes.array.isRequired,
	setNodes: PropTypes.func.isRequired,
	setCurrentStep: PropTypes.func.isRequired,
	isEditMode: PropTypes.bool,
	isViewMode: PropTypes.bool,
	onSubmit: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
	loading: PropTypes.bool,
	savedProjectId: PropTypes.number,
	nodesSaved: PropTypes.bool,
	setNodesSaved: PropTypes.func,
};

NodeInformationFormV2.defaultProps = {
	isEditMode: false,
	isViewMode: false,
	loading: false,
	savedProjectId: null,
	nodesSaved: false,
	setNodesSaved: null,
};
